import React, { useState, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { getVesselFuelOverTime } from "../../../../services/vesselMachineryService";
import { generateDateTicks } from "../../../../services/dataTicks";

const FuelConsumptionOverTime = ({ engineId, voyageId, selectedEngine }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const vesselId = currentVessel?._id;
  const { selectedEngineId } = useSelector((state) => state.mainEngine);

  const stateConfig = {
    "At sea": { color: "#7d3c98" },
    "At sea (drifting)": { color: "#a5b4fc" },
    Maneuvering: { color: "#d35400" },
    "Maneuvering (anchored)": { color: "#ff8c00" },
    "In Port": { color: "#27ae60" },
    "N/A": { color: "#cccccc" },
  };

  const getFirstState = (stateString) => {
    if (!stateString || stateString === "N/A" || stateString === "unknown" || stateString === undefined) return "N/A";
    return stateString.split("|")[0]?.trim() || "N/A";
  };

  const fetchData = async (start, end) => {
    if (!vesselId || !selectedEngineId || !start || !end) return;
    const engine = (selectedEngine || selectedEngineId || "").toUpperCase();

    setLoading(true);
    try {
      const response = await getVesselFuelOverTime(
        vesselId,
        engine,
        voyageId ? null : start,
        voyageId ? null : end,
        voyageId || null
      );

      console.log("API Response:", response);
      setChartData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching fuel data:", error);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) fetchData(startDate, endDate);
  }, [vesselId, selectedEngine, startDate, endDate, voyageId, selectedEngineId]);

  const parseDate = (dateString) => {
    const parts = dateString.split("-");
    return Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  // Create vessel state series based on actual API response
  const vesselStateSeriesArray = useMemo(() => {
    const map = {};

    chartData.forEach((item) => {
      const ts = parseDate(item.date);
      const rawState = getFirstState(item.state);
      const stateName = `Vessel State: ${rawState}`;
      const color = stateConfig[rawState]?.color || stateConfig["N/A"].color;

      if (!map[stateName]) {
        map[stateName] = {
          name: stateName,
          type: "line",
          data: [],
          dashStyle: "Dash",
          lineWidth: 3,
          color: color,
          marker: { enabled: true, radius: 4, symbol: "circle" },
          yAxis: 0,
          zIndex: 3,
          showInLegend: true,
        };
      }

      map[stateName].data.push({ 
        x: ts, 
        y: 0, // Fixed position at bottom
        state: rawState,
        color: color
      });
    });

    return Object.values(map);
  }, [chartData]);

  const fuelSeries = useMemo(() => {
    const data = chartData.map((item) => ({ 
      x: parseDate(item.date), 
      y: item.consumption || 0 
    }));
    return [
      {
        name: "Fuel Consumption",
        type: "line",
        data,
        color: "#1a5276",
        zIndex: 1,
        marker: { radius: 4 },
      },
    ];
  }, [chartData]);

  // Combine all series - only vessel states that exist in the data
  const allSeries = [...vesselStateSeriesArray, ...fuelSeries];
  const tickPositions = generateDateTicks(chartData.map((item) => item.date));

  const chartOptions = {
    chart: { 
      type: "line", 
      height: "500px", 
      backgroundColor: "transparent" 
    },
    title: { text: null },
    xAxis: {
      type: "datetime",
      tickPositions,
      title: { text: "Timeline", style: { color: "#666", fontSize: "12px" } },
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
      lineColor: "#ccd6eb",
      lineWidth: 1,
      labels: { style: { color: "#666" } },
      crosshair: { width: 1, color: "#e5e7eb", dashStyle: "Solid" },
    },
    yAxis: {
      title: { 
        text: "Fuel Consumption (MT/24hrs)", 
        style: { color: "#666", fontSize: "12px" } 
      },
      min: 0,
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
      lineColor: "#ccd6eb",
      lineWidth: 1,
      labels: { style: { color: "#666" } },
      minPadding: 0.1,
      maxPadding: 0.1,
      tickAmount: 6,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const date = Highcharts.dateFormat("%b %e, %Y", this.x);
        let html = `<div style="font-weight:600;margin-bottom:5px">${date}</div>`;

        // Show vessel state and fuel consumption in tooltip
        this.points?.forEach((p) => {
          if (p.series.name.startsWith("Vessel State:")) {
            // Get the actual state value from the point data
            const stateValue = p.point.options?.state || p.point.state || "N/A";
            const stateColor = p.point.options?.color || p.point.color || "#ccc";
            html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px">
              <span style="color:#666;display:flex;align-items:center">
                <span style="width:8px;height:8px;background:${stateColor};margin-right:6px;border-radius:2px"></span>
                Vessel State:
              </span>
              <span style="font-weight:600">${stateValue}</span>
            </div>`;
          } else if (p.series.name === "Fuel Consumption") {
            html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px">
              <span style="color:#666;display:flex;align-items:center">
                <span style="width:8px;height:8px;background:${p.color};margin-right:6px;border-radius:2px"></span>
                ${p.series.name}:
              </span>
              <span style="font-weight:600">${p.y} MT/24hrs</span>
            </div>`;
          }
        });

        return html;
      },
    },
    plotOptions: {
      line: {
        marker: { enabled: true, radius: 4 },
      },
      series: {
        states: { 
          hover: { 
            halo: { size: 0 } 
          } 
        },
      },
    },
    series: allSeries,
    legend: { 
      enabled: true,
      labelFormatter: function () {
        return this.name;
      },
    },
    credits: { enabled: false },
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1>Fuel Consumption Over Time</h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : chartData.length > 0 ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ className: "w-full", style: { height: "100%" } }}
          />
        ) : (
          <div className="flex justify-center items-center h-64 text-gray-500">
            No data available for the selected period
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelConsumptionOverTime;